import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { LocationProvider } from './LocationContext';
import { checkSafariCompatibility, addSafariPolyfills } from './utils/safariCheck';

// Debug logging
console.log('index.js starting...');

// Check Safari compatibility
const safariCheck = checkSafariCompatibility();
console.log('Safari compatibility check:', safariCheck);

// Add polyfills if needed
addSafariPolyfills();

// Global error handlers
window.onerror = function(msg, url, lineNo, columnNo, error) {
    console.error('Window error:', msg, url, lineNo, columnNo, error);
    const errorDiv = document.getElementById('error-display');
    if (errorDiv) {
        errorDiv.innerHTML = `
            <h3>JavaScript Error:</h3>
            <p><strong>Message:</strong> ${msg}</p>
            <p><strong>URL:</strong> ${url}</p>
            <p><strong>Line:</strong> ${lineNo}, Column: ${columnNo}</p>
            <pre>${error?.stack || 'No stack trace'}</pre>
        `;
        errorDiv.style.display = 'block';
    }
    return true;
};

window.addEventListener('unhandledrejection', function(event) {
    console.error('Unhandled promise rejection:', event.reason);
    const errorDiv = document.getElementById('error-display');
    if (errorDiv) {
        errorDiv.innerHTML = `
            <h3>Unhandled Promise Rejection:</h3>
            <pre>${event.reason}</pre>
        `;
        errorDiv.style.display = 'block';
    }
});

// Suppress ResizeObserver loop errors
const debounce = (callback, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => callback(...args), delay);
    };
};

const resizeObserverErr = debounce(() => {
    console.warn('ResizeObserver loop completed with undelivered notifications.');
}, 100);

window.addEventListener('error', (e) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
        resizeObserverErr();
    }
});

console.log('Creating React root...');

try {
    const rootElement = document.getElementById('root');
    if (!rootElement) {
        throw new Error('Root element not found! Make sure there is a div with id="root" in index.html');
    }
    
    console.log('Root element found, creating React root...');
    
    // For Safari compatibility, use a fallback rendering method
    if (safariCheck.isIOS && safariCheck.isSafari) {
        console.log('Using iOS Safari compatible rendering...');
        
        // Try React 18 createRoot first
        try {
            const root = ReactDOM.createRoot(rootElement);
            console.log('React 18 createRoot successful on Safari');
            
            root.render(
                <React.StrictMode>
                    <LocationProvider>
                        <App />
                    </LocationProvider>
                </React.StrictMode>
            );
            
            console.log('App rendered successfully with React 18');
        } catch (e) {
            console.error('React 18 createRoot failed, falling back to legacy render:', e);
            
            // Fallback to legacy render for older Safari
            ReactDOM.render(
                <React.StrictMode>
                    <LocationProvider>
                        <App />
                    </LocationProvider>
                </React.StrictMode>,
                rootElement
            );
            
            console.log('App rendered successfully with legacy ReactDOM.render');
        }
    } else {
        // Standard React 18 rendering for non-Safari browsers
        const root = ReactDOM.createRoot(rootElement);
        
        console.log('Rendering app...');
        root.render(
            <LocationProvider>
                <App />
            </LocationProvider>
        );
        
        console.log('App rendered successfully');
    }
} catch (error) {
    console.error('Failed to render app:', error);
    document.body.innerHTML = `
        <div style="padding: 20px; font-family: Arial;">
            <h1>Failed to load application</h1>
            <h3>Error:</h3>
            <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error.message}</pre>
            <h3>Stack:</h3>
            <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error.stack}</pre>
        </div>
    `;
}

// Register service worker for PWA and push notifications
if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
        try {
            // Register the main service worker
            const swRegistration = await navigator.serviceWorker.register('/service-worker.js');
            console.log('Service Worker registered successfully:', swRegistration.scope);
            
            // Check for updates
            swRegistration.addEventListener('updatefound', () => {
                const newWorker = swRegistration.installing;
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        console.log('New service worker available, refresh to update');
                        // You could show a notification to the user here
                    }
                });
            });
        } catch (error) {
            console.error('Service Worker registration failed:', error);
        }
    });
}
